<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { downloadFile } from '@chatwoot/utils';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { emitter } from 'shared/helpers/mitt';
import Icon from 'next/icon/Icon.vue';
import FileIcon from 'next/icon/FileIcon.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import { messageTimestamp } from 'shared/helpers/timeHelper';
import { isMediaAttachment } from './galleryUtils';

const props = defineProps({
  attachments: {
    type: Array,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
  error: {
    type: [Object, String],
    default: null,
  },
});

const { t } = useI18n();
const currentPage = ref(1);
const PAGE_SIZE = 12;

const documentsAttachments = computed(() =>
  props.attachments.filter(attachment => !isMediaAttachment(attachment))
);
const totalPages = computed(() =>
  Math.max(1, Math.ceil(documentsAttachments.value.length / PAGE_SIZE))
);
const visibleAttachments = computed(() => {
  const startIndex = (currentPage.value - 1) * PAGE_SIZE;
  return documentsAttachments.value.slice(startIndex, startIndex + PAGE_SIZE);
});
const pageIndicator = computed(
  () => `${currentPage.value} / ${totalPages.value}`
);

const readableTime = attachment => {
  if (!attachment.created_at) return '';

  return messageTimestamp(attachment.created_at, 'LLL d yyyy, h:mm a') || '';
};

const downloadAttachment = async attachment => {
  try {
    await downloadFile({
      url: attachment.data_url,
      type: attachment.file_type,
      extension: attachment.extension,
    });
  } catch {
    useAlert(t('GALLERY_VIEW.ERROR_DOWNLOADING'));
  }
};

const openMessage = attachment => {
  emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE, {
    messageId: attachment.message_id,
  });
};

const goToPreviousPage = () => {
  if (currentPage.value <= 1) return;
  currentPage.value -= 1;
};

const goToNextPage = () => {
  if (currentPage.value >= totalPages.value) return;
  currentPage.value += 1;
};

watch(totalPages, newTotalPages => {
  if (currentPage.value > newTotalPages) {
    currentPage.value = newTotalPages;
  }
});
</script>

<template>
  <div class="flex flex-col gap-2">
    <div
      v-if="loading && !documentsAttachments.length"
      class="flex items-center gap-2 justify-center p-6 text-n-slate-12"
    >
      <span class="text-sm">{{ $t('GALLERY_VIEW.LOADING') }}</span>
      <Spinner class="size-5" />
    </div>
    <p v-else-if="error && !documentsAttachments.length">
      {{ $t('GALLERY_VIEW.ERROR_LOADING_ATTACHMENTS') }}
    </p>
    <div v-else class="flex flex-col gap-2">
      <div
        v-if="loading"
        class="flex items-center justify-center gap-2 text-xs text-n-slate-11"
      >
        <Spinner class="size-4" />
        <span>{{ $t('GALLERY_VIEW.LOADING') }}</span>
      </div>

      <div
        v-if="!visibleAttachments.length"
        class="flex items-center justify-center rounded-md border border-dashed border-n-weak p-6 text-sm text-n-slate-11"
      >
        {{ $t('GALLERY_VIEW.NO_ATTACHMENTS') }}
      </div>

      <div v-else class="flex flex-col gap-2">
        <div
          v-for="attachment in visibleAttachments"
          :key="attachment.id"
          class="flex flex-col gap-3 w-full p-3 rounded-md border border-n-weak text-left hover:bg-n-slate-2 transition-colors"
        >
          <div class="flex items-center gap-3 w-full">
            <div
              class="flex items-center justify-center size-12 rounded-xl bg-n-slate-3 shrink-0"
            >
              <FileIcon
                :file-type="attachment.extension || attachment.file_type"
                class="size-7 text-n-slate-11"
              />
            </div>

            <div class="flex flex-col min-w-0 flex-1">
              <span class="truncate text-sm font-medium text-n-slate-12">
                {{ attachment.extension || attachment.file_type }}
              </span>
              <span class="truncate text-xs text-n-slate-11">
                {{
                  attachment.sender?.name || attachment.sender?.available_name
                }}
                <template v-if="readableTime(attachment)">
                  · {{ readableTime(attachment) }}
                </template>
              </span>
            </div>

            <div class="flex items-center gap-2 shrink-0">
              <button
                type="button"
                class="grid place-content-center size-8 rounded-md border border-n-weak text-n-slate-11 hover:bg-n-slate-2 transition-colors shrink-0"
                :title="$t('GALLERY_VIEW.DOWNLOAD')"
                @click="downloadAttachment(attachment)"
              >
                <Icon icon="i-lucide-download" class="size-4" />
              </button>
            </div>
          </div>
          <div class="flex justify-end">
            <button
              type="button"
              class="flex items-center justify-center gap-1.5 w-full h-8 px-3 rounded-md border border-n-weak text-xs font-medium text-n-slate-11 hover:bg-n-slate-2 transition-colors"
              :title="$t('GALLERY_VIEW.VIEW_MESSAGE')"
              @click="openMessage(attachment)"
            >
              <Icon icon="i-lucide-message-square" class="size-3.5" />
              {{ $t('GALLERY_VIEW.VIEW_MESSAGE') }}
            </button>
          </div>
        </div>
      </div>

      <div
        v-if="totalPages > 1"
        class="flex items-center justify-between gap-2 text-xs text-n-slate-11"
      >
        <button
          type="button"
          class="grid place-content-center size-8 rounded-md border border-n-weak disabled:opacity-50"
          :disabled="currentPage === 1"
          @click="goToPreviousPage"
        >
          <Icon icon="i-lucide-chevron-left" class="size-4" />
        </button>
        <span>{{ pageIndicator }}</span>
        <button
          type="button"
          class="grid place-content-center size-8 rounded-md border border-n-weak disabled:opacity-50"
          :disabled="currentPage === totalPages"
          @click="goToNextPage"
        >
          <Icon icon="i-lucide-chevron-right" class="size-4" />
        </button>
      </div>
    </div>
  </div>
</template>
